<div [formGroup]="form" *ngIf="!loading; else loadingPanel">
    <div class="card p-4 mt-4">
        <!-- Information -->
        <h3 class="mt-0 mb-1">{{ "Information" | translate }}</h3>
        <div class="row mt-2">
            <!-- Price -->
            <div class="col-12 col-lg-6 form-group" *ngIf="!isWholeSale && !hasVariants">
                <label class="form-label required" for="referencePrice">{{ "Price" | translate }}</label>
                <div class="input-group">
                    <input
                        type="number"
                        class="form-control w-50"
                        id="referencePrice"
                        formControlName="referencePrice"
                        [class.is-invalid]="isInvalid('referencePrice')"
                    />
                    <select
                        class="form-select w-50"
                        formControlName="referenceCurrency"
                        [class.is-invalid]="isInvalid('referenceCurrency')"
                    >
                        <option [value]="null" disabled>{{ "Currency" | translate }}</option>
                        <option *ngFor="let currency of currencies" [value]="currency.value">{{ currency.label | translate }}</option>
                    </select>
                </div>
            </div>
            <!-- Sample price -->
            <div class="col-12 col-lg-6 form-group" *ngIf="isWholeSale">
                <label class="form-label required" for="samplePrice">{{ "Sample Price" | translate }}</label>
                <div class="input-group">
                    <input
                        type="number"
                        class="form-control w-50"
                        id="samplePrice"
                        formControlName="samplePrice"
                        [class.is-invalid]="isInvalid('samplePrice')"
                    />
                    <select
                        class="form-select w-50"
                        formControlName="referenceCurrency"
                        [class.is-invalid]="isInvalid('referenceCurrency')"
                    >
                        <option [value]="null" disabled>{{ "Currency" | translate }}</option>
                        <option *ngFor="let currency of currencies" [value]="currency.value">{{ currency.label | translate }}</option>
                    </select>
                </div>
            </div>
            <!-- Vat -->
            <div class="col-12 col-lg-6 form-group" *ngIf="shouldShowVat()">
                <label class="form-label required" for="vat">{{ "Vat" | translate }}</label>
                <select class="form-select" id="vat" formControlName="vat" [class.is-invalid]="isInvalid('vat')">
                    <option [ngValue]="null" disabled>{{ "Select a vat" | translate }}</option>
                    <option *ngFor="let vat of vats" [ngValue]="vat.id">{{ vat.name }}</option>
                </select>
            </div>
            <!-- Stocks -->
            <div class="col-12 col-lg-6 form-group" *ngIf="!hasVariants && form.contains('stocks')">
                <label class="form-label required" for="stocks">{{ "Stocks" | translate }}</label>
                <input type="number" class="form-control" id="stocks" formControlName="stocks" [class.is-invalid]="isInvalid('stocks')" />
            </div>
            <!-- Sku -->
            <div class="col-12 col-lg-6 form-group" *ngIf="!hasVariants && form.contains('sku')">
                <label class="form-label required" for="sku">{{ "SKU" | translate }}</label>
                <div class="position-relative">
                    <input
                        type="text"
                        class="form-control"
                        id="sku"
                        formControlName="sku"
                        [class.is-invalid]="isInvalid('sku')"
                        (input)="onSkuInput(form.get('sku'), $event)"
                        (paste)="onSkuPaste(form.get('sku'), $event)"
                    />
                    <div
                        class="position-absolute"
                        style="right: 10px; top: 50%; transform: translateY(-50%)"
                        *ngIf="isSkuLoading(form.get('sku').value)"
                    >
                        <ion-spinner name="dots" style="width: 20px; height: 20px"></ion-spinner>
                    </div>
                </div>
                <div class="invalid-feedback d-block" *ngIf="form.get('sku').errors?.['uniqueSku']">
                    {{ "SKU must be unique" | translate }}
                </div>
                <div class="invalid-feedback d-block" *ngIf="form.get('sku').errors?.['skuExists']">
                    {{ "SKU is already used by another offer" | translate }}
                </div>
            </div>
            <!-- minWholesaleQuantity -->
            <div class="col-12 col-lg-6 form-group" *ngIf="isWholeSale">
                <label class="form-label required" for="minWholesaleQuantity">{{ "Minimum wholesale quantity" | translate }}</label>
                <input
                    type="number"
                    class="form-control"
                    id="minWholesaleQuantity"
                    formControlName="minWholesaleQuantity"
                    [class.is-invalid]="isInvalid('minWholesaleQuantity')"
                />
                <div
                    class="invalid-feedback d-block"
                    *ngIf="isInvalid('minWholesaleQuantity') && form.get('minWholesaleQuantity').errors?.['min']"
                >
                    {{ "The minimum order threshold must be greater than one." | translate }}
                </div>
            </div>
        </div>
    </div>
    <!-- Wholesale prices -->
    <ng-container *ngIf="isWholeSale">
        <div class="card p-4 mt-4">
            <h3 class="mt-0 mb-1">{{ "Tableau des prix" | translate }}</h3>
            <p>
                {{ "Wholesale explanation" | translate }}
            </p>
            <table class="table table-bordered table-input wholesale-price-grid mt-2">
                <tr>
                    <th class="w-25">{{ "Min quantity" | translate }}</th>
                    <th class="w-25">{{ "Max quantity" | translate }}</th>
                    <th class="w-50">{{ "Price between" | translate }}</th>
                    <th></th>
                </tr>
                <!-- Wholesale prices -->
                <ng-container *ngFor="let wholesaleForm of getWholesaleForms(); let i = index">
                    <tr [formGroup]="wholesaleForm">
                        <td class="w-25">
                            <input
                                formControlName="min"
                                type="number"
                                step="1"
                                class="form-control"
                                required
                                [class.is-invalid]="isWholesaleFieldInvalid(wholesaleForm, 'min')"
                            />
                        </td>
                        <!-- Max -->
                        <td class="w-25">
                            <input
                                formControlName="max"
                                type="number"
                                step="1"
                                class="form-control"
                                required
                                [class.is-invalid]="isWholesaleFieldInvalid(wholesaleForm, 'max')"
                            />
                            <div
                                class="invalid-feedback d-block"
                                *ngIf="showWholesaleFieldError(wholesaleForm, 'max', 'maxNotAboveMin')"
                            >
                                {{ "The maximum quantity must be greater than the minimum quantity." | translate }}
                            </div>
                        </td>
                        <!-- Price -->
                        <td class="w-50">
                            <div class="input-group">
                                <input
                                    formControlName="price"
                                    type="number"
                                    step="0.01"
                                    class="form-control"
                                    required
                                    [class.is-invalid]="isWholesaleFieldInvalid(wholesaleForm, 'price')"
                                />
                                <div class="input-group-text">
                                    {{ form.get("referenceCurrency")?.value | currencySymbol }}
                                </div>
                            </div>
                            <div
                                class="invalid-feedback d-block"
                                *ngIf="showWholesaleFieldError(wholesaleForm, 'price', 'priceNotBelowBase')"
                            >
                                {{ "The price must be lower than the base price." | translate }}
                            </div>
                            <div
                                class="invalid-feedback d-block"
                                *ngIf="showWholesaleFieldError(wholesaleForm, 'price', 'priceNotDescending')"
                            >
                                {{ "The unit prices entered must be in descending order." | translate }}
                            </div>
                        </td>
                        <!-- Remove button -->
                        <td>
                            <span
                                class="d-inline-flex clickable text-danger btn-danger btn-sm"
                                *ngIf="i === getWholesaleForms().length - 1"
                                (click)="removeWholesalePrice(wholesaleForm)"
                            >
                                <i class="bi bi-trash"></i>
                            </span>
                        </td>
                    </tr>
                </ng-container>
                <!-- Add price button -->
                <tr>
                    <td colspan="4">
                        <div class="d-flex justify-content-end">
                            <button type="button" class="btn btn-primary" (click)="addWholesalePrice()">
                                <span class="bi bi-plus"></span> {{ "Add price" | translate }}
                            </button>
                        </div>
                    </td>
                </tr>
            </table>
        </div>
    </ng-container>
    <!-- Variants -->
    <ng-container *ngIf="hasVariants">
        <div class="card p-4 mt-4">
            <div class="row align-items-start">
                <div class="col-12 col-lg-5 d-flex pb-4">
                    <h3 class="mt-0 mb-0">{{ "variants" | translate }}</h3>
                </div>
                <div class="col-12 col-lg-7 pb-4 d-flex justify-content-end align-items-center gap-2" *ngIf="hasVariantPrices">
                    <button type="button" (click)="toggleSamePriceForAllVariants()" class="btn btn-outline-primary w-50">
                        {{ !variantUniquePrice ? ("Same price for all" | translate) : ("Different price for all" | translate) }}
                    </button>
                    <!-- Currency -->
                    <select
                        class="form-select w-50"
                        formControlName="referenceCurrency"
                        [class.is-invalid]="isInvalid('referenceCurrency')"
                    >
                        <option [value]="null" disabled>{{ "Currency" | translate }}</option>
                        <option *ngFor="let currency of currencies" [value]="currency.value">{{ currency.label | translate }}</option>
                    </select>
                </div>
                <!-- Variant name -->
                <table class="table table-bordered table-input">
                    <tr>
                        <th class="w-40">{{ "Variant name" | translate }}</th>
                        <th class="w-20">{{ "SKU" | translate }}</th>
                        <th class="w-20">{{ "Stocks" | translate }}</th>
                        <th class="w-20" *ngIf="hasVariantPrices">{{ "Price" | translate }}</th>
                    </tr>
                    <tr *ngFor="let line of variantLines; let i = index" [formGroup]="line.form">
                        <!-- Name -->
                        <td class="col">
                            <div class="d-flex flex-column gap-1">
                                <span class="badge bg-transparent fw-normal text-dark me-1" *ngFor="let variantName of line.name">
                                    {{ variantName }}
                                </span>
                            </div>
                        </td>
                        <!-- Sku -->
                        <td *ngIf="line.form.contains('sku')">
                            <div class="position-relative">
                                <input
                                    type="text"
                                    class="form-control"
                                    formControlName="sku"
                                    [placeholder]="'SKU' | translate"
                                    [class.is-invalid]="isVariantSkuInvalid(line)"
                                    (input)="onSkuInput(line.form.get('sku'), $event)"
                                    (paste)="onSkuPaste(line.form.get('sku'), $event)"
                                />
                                <div
                                    class="position-absolute"
                                    style="right: 10px; top: 50%; transform: translateY(-50%)"
                                    *ngIf="isSkuLoading(line.form.get('sku').value)"
                                >
                                    <ion-spinner name="dots" style="width: 20px; height: 20px"></ion-spinner>
                                </div>
                            </div>
                            <div class="invalid-feedback d-block" *ngIf="isDuplicateVariantSku(line)">
                                {{ "SKU must be unique" | translate }}
                            </div>
                            <div class="invalid-feedback d-block" *ngIf="line.form.get('sku').errors?.['skuExists']">
                                {{ "SKU is already used by another offer" | translate }}
                            </div>
                        </td>
                        <!-- Stocks -->
                        <td *ngIf="line.form.contains('stocks')">
                            <input
                                type="number"
                                class="form-control"
                                formControlName="stocks"
                                [placeholder]="'Stocks' | translate"
                                [class.is-invalid]="line.form.get('stocks').invalid && line.form.get('stocks').touched"
                            />
                        </td>
                        <!-- Price -->
                        <td *ngIf="hasVariantPrices">
                            <div class="input-group">
                                <input
                                    type="number"
                                    class="form-control"
                                    formControlName="price"
                                    [placeholder]="'Price' | translate"
                                    [class.is-invalid]="line.form.get('price').invalid && line.form.get('price').touched"
                                />
                                <div class="input-group-text">
                                    {{ form.get("referenceCurrency")?.value | currencySymbol }}
                                </div>
                            </div>
                        </td>
                    </tr>
                </table>
            </div>
        </div>
    </ng-container>
    <!-- Delivery -->
    <ng-container *ngIf="shippable">
        <div class="card p-4 mt-4">
            <div class="row align-items-start">
                <div class="col-12 col-lg-7">
                    <h3 class="mt-0 mb-3">{{ "Delivery" | translate }}</h3>
                </div>
                <!-- Days to ship -->
                <div class="col-12 col-lg-5 form-group">
                    <label class="form-label required" for="daysToShip">{{ "Days to ship" | translate }}</label>
                    <input
                        type="number"
                        class="form-control"
                        id="daysToShip"
                        formControlName="daysToShip"
                        [class.is-invalid]="isInvalid('daysToShip')"
                    />
                    <div class="invalid-feedback d-block" *ngIf="isInvalid('daysToShip') && form.get('daysToShip').errors?.['max']">
                        {{ "The handover delay to the carrier cannot exceed 3 days." | translate }}
                    </div>
                </div>
                <div class="col-12 col-lg-5 form-group">
                    <label class="form-label" for="shippingSubsidyPercent">{{ "Share of shipping you cover" | translate }}</label>
                    <select class="form-select" id="shippingSubsidyPercent" formControlName="shippingSubsidyPercent">
                        <option *ngFor="let pct of shippingSubsidyPercentOptions" [ngValue]="pct">{{ pct }} %</option>
                    </select>
                    <p class="form-text text-muted small mb-0">{{ "Share of shipping you cover help" | translate }}</p>
                </div>
            </div>
            <div class="packages-list mt-2">
                <div class="package-card" *ngFor="let packageForm of getPackages(); let i = index" [formGroup]="packageForm">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                        <span class="fw-bold">{{ "Package" | translate }} {{ i + 1 }}</span>
                        <span class="d-inline-flex clickable text-danger" type="button" (click)="removePackage(i)">
                            <span class="bi bi-trash"></span>
                        </span>
                    </div>
                    <div class="row g-2">
                        <div class="col-12 col-md-6 form-group mb-0">
                            <label class="form-label">{{ "Weight (g)" | translate }}</label>
                            <input
                                type="number"
                                class="form-control"
                                formControlName="weight"
                                [placeholder]="'Weight in grams' | translate"
                                [class.is-invalid]="packageForm.get('weight').invalid && packageForm.get('weight').touched"
                            />
                            <div
                                class="invalid-feedback d-block"
                                *ngIf="packageForm.get('weight').touched && packageForm.get('weight').errors?.['max']"
                            >
                                {{ "The parcel weight cannot exceed 30,000 g." | translate }}
                            </div>
                        </div>
                        <div class="col-12 col-md-6 form-group mb-0">
                            <label class="form-label">{{ "Deci" | translate }}</label>
                            <app-deci-helper
                                formControlName="deci"
                                [isInvalid]="
                                    packageForm.get('deci').invalid && (packageForm.get('deci').touched || packageForm.get('deci').dirty)
                                "
                            ></app-deci-helper>
                            <div
                                class="invalid-feedback d-block"
                                *ngIf="
                                    (packageForm.get('deci').touched || packageForm.get('deci').dirty) &&
                                    packageForm.get('deci').errors?.['max']
                                "
                            >
                                {{ "The parcel deci cannot exceed 30." | translate }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="d-flex justify-content-end mt-2">
                    <button type="button" class="btn btn-primary w-100 packages-add-btn" (click)="addPackage()">
                        <span class="bi bi-plus"></span> {{ "Add package" | translate }}
                    </button>
                </div>
            </div>
            <div *ngIf="deliveryPriceEstimate || deliveryPriceEstimateLoading" class="pt-2 text-end">
                <ng-container *ngIf="!deliveryPriceEstimateLoading; else deliveryPriceEstimateLoadingPanel">
                    <p class="mb-1 small text-muted">
                        {{ "Carrier shipping cost estimate" | translate }} :
                        <span class="fw-semibold text-body"><app-price [price]="deliveryPriceEstimate" [convert]="false"></app-price></span>
                    </p>
                    <p class="fw-bold mb-1 d-flex justify-content-end align-items-center flex-wrap gap-1">
                        <span class="pe-2">{{ "Estimated shipping for seller" | translate }} : </span>
                        <app-price *ngIf="sellerDeliveryPriceEstimate" [price]="sellerDeliveryPriceEstimate" [convert]="false"></app-price>
                    </p>
                    <p class="fw-bold mb-0 d-flex justify-content-end align-items-center flex-wrap gap-1">
                        <span class="pe-2">{{ "Estimated shipping for buyer" | translate }} : </span>
                        <app-price *ngIf="buyerDeliveryPriceEstimate" [price]="buyerDeliveryPriceEstimate" [convert]="false"></app-price>
                    </p>
                </ng-container>
                <ng-template #deliveryPriceEstimateLoadingPanel>
                    <app-loader></app-loader>
                </ng-template>
            </div>
        </div>
    </ng-container>
    <div class="form-group d-flex justify-content-end pt-4">
        <button type="button" class="btn btn-lg btn-primary d-inline-flex align-items-center" (click)="submit()">
            {{ "Next" | translate }} <span class="bi ps-2 bi-arrow-right"></span>
        </button>
    </div>
</div>
<ng-template #loadingPanel>
    <div class="d-flex justify-content-center align-items-center py-4">
        <app-loader></app-loader>
    </div>
</ng-template>
